import { z } from 'zod';
import { descendantsOf, parseObject, topLevel, type NewObject, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';

/** Board objects on the system clipboard (same browser; other apps get the plain text). */
export const CLIP_MIME = 'application/x-os-board+json';
export const CLIP_MAX_OBJECTS = 2000;
export type ClipPayload = { v: 1; objects: { ref: string; data: NewObject }[] };

const payloadSchema = z.object({
  v: z.literal(1),
  objects: z.array(z.object({ ref: z.string().min(1).max(64), data: z.record(z.string(), z.unknown()) })).max(CLIP_MAX_OBJECTS),
});

/**
 * The selection with its descendants: top-level objects in world coordinates (no parent), descendants relative to
 * their copied parent, connectors with their ends (attached ends outside the copy are freed when pasted).
 */
export function serializeSelection(store: SceneStore, ids: readonly string[]): ClipPayload {
  const g = store.graph();
  const top = topLevel(g, [...ids]).filter((id) => store.getObject(id));
  const all = new Set([...top, ...top.flatMap((id) => descendantsOf(g, id))]);
  const objects = store
    .list()
    .filter((o) => all.has(o.id))
    .map((o) => {
      const { id, z: _z, ...rest } = structuredClone(o) as typeof o & { z: string };
      void _z;
      const root = top.includes(id);
      const at = root ? g.origin.get(id) : undefined;
      const data = (root ? { ...rest, parentId: null, ...(at && o.type !== 'connector' ? { x: at.x, y: at.y } : {}) } : rest) as NewObject;
      return { ref: id, data };
    });
  return { v: 1, objects };
}

/** A payload read back from the clipboard, or null if anything in it is malformed or too big. */
export function parseClipPayload(raw: string): ClipPayload | null {
  try {
    const parsed = payloadSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.objects.length === 0) return null;
    for (const o of parsed.data.objects) if (!parseObject({ ...o.data, id: 'probe', z: 'a0' })) return null;
    return parsed.data as ClipPayload;
  } catch {
    return null;
  }
}

/** Bounds of the payload's top-level objects (world, as copied), or null when it holds none. */
export function payloadBounds(payload: ClipPayload): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const refs = new Set(payload.objects.map((o) => o.ref));
  const roots = payload.objects.map((o) => o.data).filter((d) => d.parentId === null || !refs.has(d.parentId));
  const xs: number[] = [];
  const ys: number[] = [];
  for (const d of roots) {
    if (d.type === 'connector') {
      xs.push(d.start.x, d.end.x);
      ys.push(d.start.y, d.end.y);
    } else {
      xs.push(d.x, d.x + d.w);
      ys.push(d.y, d.y + d.h);
    }
  }
  if (!xs.length) return null;
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/** Offset that centres the payload's top-level objects on `at`. */
export function pasteOffset(payload: ClipPayload, at: Point): Point {
  const b = payloadBounds(payload);
  return b ? { x: at.x - (b.minX + b.maxX) / 2, y: at.y - (b.minY + b.maxY) / 2 } : { x: 0, y: 0 };
}

/** Plain text for other apps: one line per object that has text (texts, notes, labels, links, tables, task lists). */
export function clipboardText(store: SceneStore, ids: readonly string[]): string {
  return ids
    .map((id) => store.getObject(id))
    .map((o) => {
      if (!o) return '';
      if (o.type === 'text' || o.type === 'sticky' || o.type === 'shape') return o.text ?? '';
      if (o.type === 'link' || o.type === 'embed') return o.url;
      if (o.type === 'section') return o.name;
      if (o.type === 'table') return o.rows.map((r) => o.cols.map((c) => r.cells[c.id] ?? '').join('\t')).join('\n');
      if (o.type === 'tasklist') return [o.title, ...o.items.map((t) => `${'  '.repeat(t.indent)}${t.done ? '☑' : '☐'} ${t.text}`)].filter((l) => l.trim() !== '').join('\n');
      return '';
    })
    .filter((s) => s.trim() !== '')
    .join('\n');
}

/** The last board copy of this tab, for the context menu's Paste (reading the system clipboard needs a permission). */
let lastCopy: ClipPayload | null = null;
export const rememberCopy = (p: ClipPayload) => void (lastCopy = p);
export const lastCopied = (): ClipPayload | null => lastCopy;
